import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../types/auth';
import { JobCard, Task, Signature, AssetToRepair, TaskEquipment } from '../types/jobcard';
import { getJobCard, updateJobCard, getTasks, getSignatures, deleteSignature, deleteTask, getJobCardEquipment, getATRItems, completeATRItem, deleteTaskPicture, deleteJobCard, syncATRItems, completeJobCardAsync, linkJobCards, undoLinkJobCards, canUndoLink } from '../services/jobcardApi';
import { listGrns, deleteGrn } from '../services/grnApi';
import type { Grn } from '../types/grn';
import GrnForm from '../components/grn/GrnForm';
import GrnFinalizeModal from '../components/grn/GrnFinalizeModal';
import GrnViewModal from '../components/grn/GrnViewModal';
import GrnTable from '../components/grn/GrnTable';
import { ArrowLeft, RefreshCw, Plus, ChevronDown, ChevronRight, Link, Undo2, AlertTriangle } from 'lucide-react';
import { Store } from '../types/store';
import { getStores } from '../services/storeApi';
import { getSuppliers, Supplier } from '../services/supplierApi';
import CreateTaskModal from '../components/modals/CreateTaskModal';
import CreateSignatureModal from '../components/modals/CreateSignatureModal';
import EditTaskModal from '../components/modals/EditTaskModal';
import LinkJobCardModal from '../components/modals/LinkJobCardModal';
import ConfirmLinkJobCardModal from '../components/modals/ConfirmLinkJobCardModal';
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from '../utils/toast';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import ConfirmCompleteModal from '../components/modals/ConfirmCompleteModal';
import ConfirmATRCompletionModal from '../components/modals/ConfirmATRCompletionModal';
import { useAuth } from '../hooks/useAuth';
import { getUsers } from '../services/userApi';
import TaskImageModal from '../components/modals/TaskImageModal';
import TaskImagesGallery from '../components/TaskImagesGallery';
import ImageDebugInfo from '../components/ImageDebugInfo';
// import SerialNumberModal from "../components/modals/SerialNumberModal";
import { getTaskImageUrl, getSignatureImageUrl } from '../config';
import ATRItemsBox from '../components/ATRItemsBox';
import ATRItemsEmbedded from '../components/ATRItemsEmbedded';
// import config from '../config';

interface ApiError {
    response?: {
        data: {
            message?: string;
            errors?: Record<string, string[]>;
            missing_fields?: string[];
            warnings?: string[];
            task_count?: number;
            incomplete_atr_count?: number;
        };
    };
    message?: string;
    code?: string; // For timeout
}

// Helper function to check if a job card is from ATERA (contains "ticket" in order number)
const isAteraJobCard = (jobCard: JobCard | null): boolean => {
    return jobCard?.order_number ? jobCard.order_number.toLowerCase().includes('ticket') : false;
};

export default function EditJobCardPage() {
    const { isAdmin, user } = useAuth();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [jobCard, setJobCard] = useState<JobCard | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stores, setStores] = useState<Store[]>([]);
    const [isLoadingStores, setIsLoadingStores] = useState(true);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [signatures, setSignatures] = useState<Signature[]>([]);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [showEditTaskModal, setShowEditTaskModal] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [signatureToDelete, setSignatureToDelete] = useState<number | null>(null);
    const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [atrItems, setAtrItems] = useState<AssetToRepair[]>([]);
    const [isLoadingATRItems, setIsLoadingATRItems] = useState(false);
    const [isSyncingATRItems, setIsSyncingATRItems] = useState(false);
    const [isATRCompletionModalOpen, setIsATRCompletionModalOpen] = useState(false);
    const [selectedATRItem, setSelectedATRItem] = useState<AssetToRepair | null>(null);
    // const [isSerialNumberModalOpen, setIsSerialNumberModalOpen] = useState(false);
    // const [selectedEquipment, setSelectedEquipment] = useState<ATREquipment | null>(null);
    const [expandedEquipment, setExpandedEquipment] = useState<Record<number, boolean>>({});
    const [isDeleteJobCardModalOpen, setIsDeleteJobCardModalOpen] = useState(false);
    const [isDeletingJobCard, setIsDeletingJobCard] = useState(false);


    // Link Job Card states
    const [showLinkJobCardModal, setShowLinkJobCardModal] = useState(false);
    const [showConfirmLinkModal, setShowConfirmLinkModal] = useState(false);
    const [selectedTargetJobCard, setSelectedTargetJobCard] = useState<JobCard | null>(null);
    const [isLinking, setIsLinking] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [undoExpiresAt, setUndoExpiresAt] = useState<string | null>(null);

    // GRN states
    const [grns, setGrns] = useState<Grn[]>([]);
    const [showGrnForm, setShowGrnForm] = useState(false);
    const [editingGrn, setEditingGrn] = useState<Grn | null>(null);
    const [showGrnFinalizeModal, setShowGrnFinalizeModal] = useState(false);
    const [grnToFinalize, setGrnToFinalize] = useState<Grn | null>(null);
    const [viewingGrn, setViewingGrn] = useState<Grn | null>(null);

    // Coachmark state for Add Task button (persisted via localStorage per job card)
    const [showAddTaskCoachmark, setShowAddTaskCoachmark] = useState<boolean>(() => {
        try {
            const key = `coachmark_add_task_dismissed_${id}`;
            return !localStorage.getItem(key);
        } catch {
            return true;
        }
    });

    const [formData, setFormData] = useState({
        store_id: 0,
        supplier_id: 0,
        date: '',
        time_in: '',
        time_out: '',
        travel_distance: 0,
        order_number: '',
        user_id: 0
    });

    const hasEquipmentAccess = useMemo(() => {
        return Boolean(isAdmin || user?.position?.department?.name === 'IT Department');
    }, [isAdmin, user?.position?.department?.name]);



    useEffect(() => {
        const fetchData = async () => {
            const loadingToastId = showLoadingToast('Loading job card data...');
            try {
                setIsLoading(true);
                setIsLoadingStores(true);
                setIsLoadingATRItems(true);
                if (isAdmin) setIsLoadingUsers(true);

                // First fetch the critical data
                try {
                    const jobCardRes = await getJobCard(Number(id));
                    const jobCardData = jobCardRes.data.data;
                    
                    // Check if non-admin user is trying to edit a closed job card
                    if (!isAdmin && jobCardData.closed) {
                        showErrorToast('Access Denied: Only administrators can edit closed job cards.');
                        navigate(`/jobcards/view/${id}`);
                        return;
                    }
                    
                    setJobCard(jobCardData);
                    setFormData({
                        store_id: jobCardData.store_id || 0,
                        supplier_id: jobCardData.supplier_id || 0,
                        date: jobCardData.date || '',
                        time_in: jobCardData.time_in || '',
                        time_out: jobCardData.time_out || '',
                        travel_distance: jobCardData.travel_distance || 0,
                        order_number: jobCardData.order_number || '',
                        user_id: jobCardData.user_id || 0
                    });

                    // Check if we need to sync ATR items
                    if (jobCardData.order_number) {
                        try {
                            const syncToastId = showLoadingToast('Syncing ATR items...');
                            const syncResponse = await syncATRItems(Number(id));
                            dismissToast(syncToastId);
                            if (syncResponse?.data?.message?.toLowerCase().includes('no assets found')) {
                                showSuccessToast('No ATR items found for this order.');
                                setAtrItems([]);
                            } else {
                                showSuccessToast('ATR items synced successfully');
                            }
                        } catch (e: unknown) {
                            const error = e as ApiError;
                            const msg = error?.response?.data?.message || error?.message || '';
                            if (msg.toLowerCase().includes('no assets found')) {
                                showSuccessToast('No ATR items found for this order.');
                                setAtrItems([]);
                            } else {
                                showErrorToast('Failed to sync ATR items');
                            }
                        }
                    }
                } catch (err) {
                    console.error('Error fetching job card:', err);
                    setError('Failed to load job card');
                    showErrorToast('Failed to load job card data');
                    throw err;
                }

                // Now fetch the rest of the data in parallel
                const fetchPromises = [
                    getTasks(Number(id))
                        .then(tasksRes => {
                            setTasks(tasksRes.data || []);
                        })
                        .catch(err => {
                            console.error('Error fetching tasks:', err);
                            showErrorToast('Failed to load tasks');
                            setTasks([]);
                        }),
                    
                    getSignatures(Number(id))
                        .then(signaturesRes => {
                            const signaturesData = signaturesRes.data?.data || [];
                            setSignatures(signaturesData);
                        })
                        .catch(err => {
                            console.error('Error fetching signatures:', err);
                            showErrorToast('Failed to load signatures');
                            setSignatures([]);
                        }),
                    
                    getStores()
                        .then(storesRes => {
                            setStores(storesRes.data.data || []);
                        })
                        .catch(err => {
                            console.error('Error fetching stores:', err);
                            showErrorToast('Failed to load stores');
                            setStores([]);
                        }),
                    
                    getSuppliers()
                        .then(suppliersRes => {
                            setSuppliers(suppliersRes.data.data || []);
                        })
                        .catch(err => {
                            console.error('Error fetching suppliers:', err);
                            showErrorToast('Failed to load suppliers');
                            setSuppliers([]);
                        }),
                    
                    getJobCardEquipment(Number(id))
                        .catch(err => {
                            console.error('Error fetching equipment:', err);
                        }),
                    
                    // ATR items fetching with error handling
                    getATRItems(Number(id))
                        .then(atrItemsRes => {
                            const atrItemsData = atrItemsRes?.data?.data?.data || [];
                            setAtrItems(atrItemsData);
                        })
                        .catch(err => {
                            console.error('Error fetching ATR items:', err);
                            showErrorToast('Failed to load ATR items');
                            setAtrItems([]);
                        }),

                    // GRN items fetching
                    listGrns(Number(id))
                        .then(grnsRes => {
                            setGrns(grnsRes || []);
                        })
                        .catch(err => {
                            console.error('Error fetching GRNs:', err);
                            showErrorToast('Failed to load GRNs');
                            setGrns([]);
                        })
                ];
                
                // If admin, also fetch users
                if (isAdmin) {
                    fetchPromises.push(
                        getUsers()
                            .then(usersRes => {
                                setUsers(usersRes.data);
                            })
                            .catch(err => {
                                console.error('Error fetching users:', err);
                                showErrorToast('Failed to load users');
                                setUsers([]);
                            })
                    );
                }

                // Wait for all promises to settle
                await Promise.allSettled(fetchPromises);

            } catch (err) {
                console.error('Error fetching data:', err);
                if (!error) {
                    setError('Failed to load job card');
                    showErrorToast('Failed to load job card data');
                }
            } finally {
                setIsLoading(false);
                setIsLoadingStores(false);
                setIsLoadingATRItems(false);
                if (isAdmin) setIsLoadingUsers(false);
                dismissToast(loadingToastId);
            }
        };

        fetchData();
    }, [id, isAdmin, error, navigate]);

    const isStoreUser = useMemo(() => jobCard?.user?.utype === 'STORE', [jobCard]);

    // Check if job card can be unlinked
    const checkUndoStatus = useCallback(async () => {
        if (!jobCard?.linked_from_job_card_id) return;

        try {
            const response = await canUndoLink(jobCard.id);
            setCanUndo(response.data.can_undo);
            setUndoExpiresAt(response.data.undo_expires_at);
        } catch (error) {
            console.error('Error checking undo status:', error);
        }
    }, [jobCard?.linked_from_job_card_id, jobCard?.id]);

    // Check undo status when job card is loaded
    useEffect(() => {
        if (jobCard) {
            checkUndoStatus();
        }
    }, [jobCard, checkUndoStatus]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'store_id' || name === 'supplier_id' || name === 'travel_distance' || name === 'user_id'
                ? Number(value)
                : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        const loadingToastId = showLoadingToast('Saving changes...');
        try {
            const formattedData = {
                ...formData,
                time_in: formData.time_in.substring(0, 5),
                time_out: formData.time_out.substring(0, 5)
            };

            await updateJobCard(Number(id), formattedData);

            // Refresh job card data
            const updatedJobCard = await getJobCard(Number(id));
            setJobCard(updatedJobCard.data.data);

            showSuccessToast('Job card updated successfully');
        } catch (e: unknown) {
            const err = e as ApiError;
            console.log('Error details:', err.response?.data);
            if (err.response?.data?.errors) {
                const errorMessages = Object.values(err.response.data.errors).flat();
                const errorMessage = errorMessages.join(', ');
                setError(errorMessage);
                showErrorToast(errorMessage);
            } else {
                const errorMessage = 'Failed to update job card: ' + (err.response?.data?.message || err.message);
                setError(errorMessage);
                showErrorToast(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
            dismissToast(loadingToastId);
        }
    };

    // Camera handling functions
    const startCamera = async () => {
        try {
            // Stop any existing stream first
            if (videoRef.current?.srcObject) {
                const stream = videoRef.current.srcObject as MediaStream;
                stream.getTracks().forEach(track => track.stop());
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { exact: facingMode },  // Use exact to force the facing mode
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    aspectRatio: { ideal: 16 / 9 }
                }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                // Remove any transform since we want the image as-is
                videoRef.current.style.transform = 'none';
                videoRef.current.style.width = '100%';
                videoRef.current.style.height = 'auto';
                videoRef.current.style.maxHeight = '80vh';
                videoRef.current.style.objectFit = 'contain';
                videoRef.current.style.maxWidth = '100vw';

                videoRef.current.play().catch(err => {
                    console.error('Error playing video:', err);
                });
            }
            setShowCamera(true);
        } catch (err) {
            console.error('Error accessing camera:', err);
            // If exact facing mode fails, try without exact constraint
            if (err instanceof Error && err.message.includes('exact')) {
                try {
                    const fallbackStream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            facingMode: facingMode,  // Try without exact constraint
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                            aspectRatio: { ideal: 16 / 9 }
                        }
                    });

                    if (videoRef.current) {
                        videoRef.current.srcObject = fallbackStream;
                        videoRef.current.style.transform = 'none';
                        videoRef.current.style.width = '100%';
                        videoRef.current.style.height = 'auto';
                        videoRef.current.style.maxHeight = '80vh';
                        videoRef.current.style.objectFit = 'contain';
                        videoRef.current.style.maxWidth = '100vw';

                        videoRef.current.play().catch(err => {
                            console.error('Error playing video:', err);
                        });
                    }
                    setShowCamera(true);
                    return;
                } catch (fallbackErr) {
                    console.error('Fallback camera access failed:', fallbackErr);
                }
            }
            showErrorToast('Failed to access camera. Please check your permissions.');
        }
    };

    const handleSwapCamera = async () => {
        setFacingMode(current => current === 'environment' ? 'user' : 'environment');
        await startCamera();
    };

    const handleDeleteSignature = async (signatureId: number) => {
        setSignatureToDelete(signatureId);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteSignature = async () => {
        if (!signatureToDelete) return;

        try {
            console.log('Frontend: Attempting to delete signature', {
                signatureId: signatureToDelete,
                jobCardId: Number(id),
                userId: user?.id,
                isAdmin
            });
            
            await deleteSignature(signatureToDelete, Number(id));
            const updatedSignatures = await getSignatures(Number(id));
            setSignatures(updatedSignatures.data.data);
            showSuccessToast('Signature deleted successfully');
        } catch (error) {
            console.error('Frontend: Error deleting signature', error);
            showErrorToast('Error deleting signature');
        } finally {
            setIsDeleteModalOpen(false);
            setSignatureToDelete(null);
        }
    };

    const handleComplete = async () => {
        try {
            // Show a more detailed loading message for large job cards
            const equipmentCount = tasks.reduce((total, task) => total + (task.equipment?.length || 0), 0);
            const loadingMessage = equipmentCount > 50 
                ? `Completing job card with ${equipmentCount} equipment items. This may take a few minutes...`
                : 'Completing job card...';
            
            const loadingToastId = showLoadingToast(loadingMessage);
            
            // For job cards with many equipment items, use async completion
            if (equipmentCount > 100) {
                await completeJobCardAsync(Number(id));
                dismissToast(loadingToastId);
                showSuccessToast('Job card completion started. Equipment allocation will be processed in the background.');
                navigate('/jobcards');
            } else {
                // For smaller job cards, use the regular completion with timeout
                const timeoutMs = Math.max(120000, equipmentCount * 1000); // At least 2 minutes, or 1 second per equipment item
                
                await updateJobCard(Number(id), { 
                    ...formData, 
                    closed: true 
                }, {
                    timeout: timeoutMs
                });
                
                dismissToast(loadingToastId);
                showSuccessToast('Job card completed successfully');
                navigate('/jobcards');
            }
        } catch (e: unknown) {
            const error = e as ApiError;
            console.error('Error completing job card:', error);
            
            // Handle specific error cases
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                showErrorToast('The completion process is taking longer than expected. Please check if the job card was completed and refresh the page.');
            } else if (error.response && 'status' in error.response && (error.response as { status: number }).status === 503) {
                showErrorToast('Server is temporarily unavailable. The job card may still be processing. Please wait a moment and refresh the page to check the status.');
            } else if (error.response && 'status' in error.response && (error.response as { status: number }).status === 422) {
                // Handle validation errors
                const responseData = error.response?.data;
                const msg = responseData?.message || error?.message || '';
                showErrorToast('Cannot complete job card: ' + msg);
                
                // If it's a no tasks error, close the modal
                if ((responseData as { missing_fields?: string[] })?.missing_fields?.includes('no_tasks')) {
                    setIsCompleteModalOpen(false);
                }
            } else {
                const msg = error?.response?.data?.message || error?.message || '';
                showErrorToast('Failed to complete job card: ' + msg);
            }
        }
    };

    const fetchJobCard = async () => {
        try {
            const [jobCardRes, tasksRes] = await Promise.all([
                getJobCard(Number(id)),
                getTasks(Number(id))
            ]);
            setJobCard(jobCardRes.data.data);
            setTasks(tasksRes.data || []);
        } catch (error) {
            console.error('Error refreshing job card:', error);
            showErrorToast('Failed to refresh job card data');
        }
    };

    const handleDeleteTask = async (task: Task) => {
        setTaskToDelete(task);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteTask = async () => {
        if (!taskToDelete) return;

        try {
            await deleteTask(Number(id), taskToDelete.id);
            showSuccessToast('Task deleted successfully');
            // Refresh tasks
            const tasksRes = await getTasks(Number(id));
            setTasks(tasksRes.data || []);
        } catch {
            showErrorToast('Failed to delete task');
        } finally {
            setIsDeleteModalOpen(false);
            setTaskToDelete(null);
        }
    };

    const tasksWithImages = tasks.filter(task => task.pictures && task.pictures.length > 0);

    const handleNextImage = () => {
        setCurrentImageIndex(prev => Math.min(prev + 1, tasksWithImages.length - 1));
    };

    const handlePrevImage = () => {
        setCurrentImageIndex(prev => Math.max(prev - 1, 0));
    };

    const showATRCompletionModal = (item: AssetToRepair) => {
        setSelectedATRItem(item);
        setIsATRCompletionModalOpen(true);
    };

    const handleCompleteATRItem = async (itemId: number, completionAction: 'repaired' | 'received') => {
        try {
            const loadingToastId = showLoadingToast('Completing item...');
            await completeATRItem(itemId, true, completionAction); // Always create task
            // Update the local state
            setAtrItems(prev => prev.map(item => 
                item.id === itemId ? { ...item, completed: true } : item
            ));
            dismissToast(loadingToastId);
            showSuccessToast('Item completed successfully');
            // Refresh tasks since task is always created
            const tasksRes = await getTasks(Number(id));
            setTasks(tasksRes.data || []);
        } catch (error) {
            console.error('Error completing ATR item:', error);
            showErrorToast('Failed to complete item');
        }
    };

    const handleDeletePicture = async (taskId: number, pictureId: number) => {
        try {
            await deleteTaskPicture(taskId, pictureId);
            showSuccessToast("Picture deleted successfully");
            // Refresh the tasks to update the UI
            const tasksRes = await getTasks(Number(id));
            setTasks(tasksRes.data);
        } catch (error) {
            console.error('Error deleting picture:', error);
            showErrorToast("Failed to delete picture");
        }
    };

    // const showSerialNumberModal = (equipment: ATREquipment) => {
    //     setSelectedEquipment(equipment);
    //     setIsSerialNumberModalOpen(true);
    // };

    // const fetchATRItems = async () => {
    //     try {
    //         setIsLoadingATRItems(true);
    //         const response = await getATRItems(Number(id));
    //         const items = response.data.data.data || [];
    //         setAtrItems(items);
    //     } catch (error) {
    //         console.error('Error fetching ATR items:', error);
    //         showErrorToast('Failed to fetch ATR items');
    //     } finally {
    //         setIsLoadingATRItems(false);
    //     }
    // };

    const toggleEquipmentSection = (taskId: number) => {
        setExpandedEquipment(prev => ({
            ...prev,
            [taskId]: !prev[taskId]
        }));
    };

    const handleDeleteJobCard = () => {
        setIsDeleteJobCardModalOpen(true);
    };

    const confirmDeleteJobCard = async () => {
        if (!jobCard) return;

        try {
            setIsDeletingJobCard(true);
            const loadingToastId = showLoadingToast('Deleting job card...');
            
            await deleteJobCard(jobCard.id);
            
            dismissToast(loadingToastId);
            showSuccessToast('Job card deleted successfully');
            navigate('/jobcards');
        } catch (e: unknown) {
            const error = e as ApiError;
            console.error('Error deleting job card:', error);
            showErrorToast('Failed to delete job card: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsDeletingJobCard(false);
            setIsDeleteJobCardModalOpen(false);
        }
    };

    // Link Job Card handlers
    const handleLinkJobCard = () => {
        if (!jobCard?.order_number) {
            showErrorToast('Only job cards with order numbers can be linked');
            return;
        }
        setShowLinkJobCardModal(true);
    };

    const handleSelectTargetJobCard = (targetJobCard: JobCard) => {
        setSelectedTargetJobCard(targetJobCard);
        setShowLinkJobCardModal(false);
        setShowConfirmLinkModal(true);
    };

    const handleConfirmLink = async () => {
        if (!jobCard || !selectedTargetJobCard) return;

        try {
            setIsLinking(true);
            const loadingToastId = showLoadingToast('Linking job cards...');

            await linkJobCards(jobCard.id, selectedTargetJobCard.id);
            
            dismissToast(loadingToastId);
            showSuccessToast('Job cards linked successfully');
            
            // Navigate to the linked job card (the completed one that now has the order number)
            navigate(`/jobcards/edit/${selectedTargetJobCard.id}`);
        } catch (e: unknown) {
            const error = e as ApiError;
            console.error('Error linking job cards:', error);
            showErrorToast('Failed to link job cards: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsLinking(false);
            setShowConfirmLinkModal(false);
            setSelectedTargetJobCard(null);
        }
    };

    const handleUndoLink = async () => {
        if (!jobCard) return;

        try {
            const loadingToastId = showLoadingToast('Undoing job card link...');

            await undoLinkJobCards(jobCard.id);
            
            dismissToast(loadingToastId);
            showSuccessToast('Job card link undone successfully');
            
            // Refresh the page to show updated data
            window.location.reload();
        } catch (e: unknown) {
            const error = e as ApiError;
            console.error('Error undoing job card link:', error);
            showErrorToast('Failed to undo job card link: ' + (error.response?.data?.message || error.message));
        }
    };

    // GRN handlers
    const handleGrnSave = (grn: Grn) => {
        setGrns(prev => {
            const existingIndex = prev.findIndex(g => g.id === grn.id);
            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = grn;
                return updated;
            } else {
                return [...prev, grn];
            }
        });
        setShowGrnForm(false);
        setEditingGrn(null);
        // Success toast is now handled in GrnForm component
    };

    const handleEditGrn = (grn: Grn) => {
        if (grn.status === 'finalized' && !isAdmin) {
            showErrorToast('Cannot edit a finalized GRN');
            return;
        }
        setEditingGrn(grn);
        setShowGrnForm(true);
    };

    const handleFinalizeGrn = (grn: Grn) => {
        setGrnToFinalize(grn);
        setShowGrnFinalizeModal(true);
    };

    const handleViewGrn = (grn: Grn) => {
        setViewingGrn(grn);
    };

    const handleGrnFinalized = (grn: Grn) => {
        setGrns(prev => prev.map(g => g.id === grn.id ? grn : g));
        setShowGrnFinalizeModal(false);
        setGrnToFinalize(null);
        showSuccessToast('GRN finalized successfully');
    };

    const handleDeleteGrn = async (grn: Grn) => {
        if (grn.status === 'finalized' && !isAdmin) {
            showErrorToast('Cannot delete a finalized GRN');
            return;
        }

        if (!grn.id) {
            showErrorToast('GRN ID not found');
            return;
        }

        const confirmMessage = grn.status === 'finalized' 
            ? `Are you sure you want to delete finalized GRN ${grn.grn_number || 'Draft'}? This action cannot be undone.`
            : `Are you sure you want to delete GRN ${grn.grn_number || 'Draft'}?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            await deleteGrn(grn.id!);
            // Update local state
            setGrns(prev => prev.filter(g => g.id !== grn.id));
            showSuccessToast('GRN deleted successfully');
        } catch (error) {
            console.error('Error deleting GRN:', error);
            showErrorToast('Failed to delete GRN');
        }
    };

    /**
     * Determines if the current user can delete the job card
     * 
     * Rules:
     * 1. Admins can delete any job card regardless of ATR items or closed status
     * 2. Regular users can only delete their own job cards if there are no ATR items
     * 
     * @returns {boolean} True if user can delete the job card
     */
    const canDeleteJobCard = () => {
        if (!jobCard || !user) return false;
        
        // Convert IDs to strings for comparison to avoid type mismatch
        const jobCardUserId = String(jobCard.user_id);
        const currentUserId = String(user.id);
        
        // Case 1: User is an admin
        // Admins can delete job cards regardless of ATR items or closed status (full admin control)
        const adminCanDelete = isAdmin;
        
        // Case 2: Current user is the owner of the job card
        // Regular users can only delete if there are no ATR items
        const ownerCanDelete = jobCardUserId === currentUserId && atrItems.length === 0;
        
        // console.log('Job Card User ID:', jobCardUserId);
        // console.log('Current User ID:', currentUserId);
        // console.log('IDs match?', jobCardUserId === currentUserId);
        // console.log('Admin Can Delete:', adminCanDelete);
        // console.log('Owner Can Delete:', ownerCanDelete);
        // console.log('ATR Items Count:', atrItems.length);
        
        return adminCanDelete || ownerCanDelete;
    };

    if (isLoading) return <div className="text-center py-8 text-gray-600 dark:text-gray-400">Loading...</div>;
    if (!jobCard) return <div>Job card not found</div>;

    return (
        <div className="space-y-8 p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {error && (
                <div className="text-red-600 text-center mb-4">{error}</div>
            )}
            <div className="space-y-4">
                <div className="flex items-center gap-4 justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/jobcards')}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200"
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Edit</h1>
                                {jobCard?.job_number && (
                                    <span className="text-yellow-600 dark:text-yellow-500 font-semibold">
                                        #{jobCard.job_number}
                                    </span>
                                )}
                                {/* {isAdmin && jobCard && !jobCard.closed or job card.user.id is the same as the current user.id && ( */}
                                
                                {canDeleteJobCard() && (
                                    <button
                                        onClick={handleDeleteJobCard}
                                        className="ml-4 px-3 py-1 text-white bg-red-600 dark:bg-red-500 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors text-sm font-medium"
                                        disabled={isDeletingJobCard}
                                    >
                                        Delete
                                    </button>
                                )}
                                
                                {/* Link Job Card button - only show for PO job cards (with order numbers) */}
                                {(isAdmin || user?.position?.department?.name === 'IT Department') && jobCard?.order_number && !jobCard?.closed && (
                                    <button
                                        onClick={handleLinkJobCard}
                                        className="ml-2 px-3 py-1 text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm font-medium flex items-center gap-1"
                                    >
                                        <Link size={16} />
                                        Link
                                    </button>
                                )}
                                
                                {/* Undo Link button - only show for linked job cards that can be undone */}
                                {(isAdmin || user?.position?.department?.name === 'IT Department') && jobCard?.linked_from_job_card_id && canUndo && (
                                    <button
                                        onClick={handleUndoLink}
                                        className="ml-2 px-3 py-1 text-white bg-orange-600 dark:bg-orange-500 rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 transition-colors text-sm font-medium flex items-center gap-1"
                                        title={`Undo link (expires ${undoExpiresAt ? new Date(undoExpiresAt).toLocaleDateString() : 'soon'})`}
                                    >
                                        <Undo2 size={16} />
                                        Undo Link
                                    </button>
                                )}
                            </div>
                            {jobCard?.user && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    {jobCard.closed == true ? 'Completed by' : 'Assigned to'} {jobCard.user.name} ({jobCard.user.email})
                                    {jobCard.closed == true && jobCard.date_closed && (
                                        <span className="block mt-1">
                                            Closed on: {new Date(jobCard.date_closed).toLocaleDateString()}
                                        </span>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                    
                </div>
            </div>

            <form onSubmit={handleSubmit} className="max-w-2xl space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                {/* Admin-only editing warning for closed job cards */}
                {isAdmin && jobCard?.closed && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
                                    Closed Job Card - Admin Edit Mode
                                </h3>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                    This job card is closed/completed. Only administrators can make changes to closed job cards.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                
                {atrItems.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                    Please update the following information:
                                </h3>
                                <div className="mt-2 text-sm text-blue-700 dark:text-blue-200">
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Date of service</li>
                                        <li>Time In - when work started</li>
                                        <li>Time Out - when work was completed</li>
                                        <li>Travel Distance in kilometers</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-1">
                        <label htmlFor="user_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {isStoreUser ? 'Supplier' : 'Technician'}
                        </label>
                        {isStoreUser ? (
                            <select
                                id="supplier_id"
                                name="supplier_id"
                                value={formData.supplier_id}
                                onChange={handleChange}
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                disabled={!isAdmin && atrItems.length > 0}
                            >
                                <option value={0}>Select a supplier</option>
                                {suppliers.map(supplier => (
                                    <option key={supplier.id} value={supplier.id}>
                                        {supplier.name} {supplier.code && `(${supplier.code})`}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <select
                                id="user_id"
                                name="user_id"
                                value={formData.user_id}
                                onChange={handleChange}
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                disabled={!isAdmin || isLoadingUsers}
                            >
                                <option value={0}>Select Technician</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {!isAdmin && atrItems.length > 0 && (
                            <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-500">
                                Supplier cannot be changed when ATR items are present (Admin can override)
                            </p>
                        )}
                        {isAdmin && atrItems.length > 0 && (
                            <p className="mt-1 text-sm text-blue-600 dark:text-blue-500">
                                Admin: ATR items restriction bypassed - supplier can be modified
                            </p>
                        )}
                    </div>
                    <div className="md:col-span-1">
                        <label htmlFor="store_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Store
                        </label>
                        <select
                            id="store_id"
                            name="store_id"
                            value={formData.store_id}
                            onChange={handleChange}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            disabled={isLoadingStores || (!isAdmin && atrItems.length > 0)}
                        >
                            <option value={0}>Select a store</option>
                            {stores.map(store => (
                                <option key={store.id} value={store.id}>
                                    {store.name} ({store.storenumber})
                                </option>
                            ))}
                        </select>
                        {!isAdmin && atrItems.length > 0 && (
                            <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-500">
                                Store cannot be changed when ATR items are present (Admin can override)
                            </p>
                        )}
                        {isAdmin && atrItems.length > 0 && (
                            <p className="mt-1 text-sm text-blue-600 dark:text-blue-500">
                                Admin: ATR items restriction bypassed - store can be modified
                            </p>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date *
                    </label>
                    <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Time In *
                        </label>
                        <input
                            type="time"
                            name="time_in"
                            value={formData.time_in}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Time Out *
                        </label>
                        <input
                            type="time"
                            name="time_out"
                            value={formData.time_out}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Travel Distance (km) *
                    </label>
                    <input
                        type="number"
                        name="travel_distance"
                        value={formData.travel_distance}
                        onChange={handleChange}
                        required
                        min="0"
                        step="0.1"
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Order Number
                    </label>
                    <input
                        type="text"
                        name="order_number"
                        value={formData.order_number}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!isAdmin}
                    />
                    {!isAdmin && (
                        <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-500">
                            Order number can only be modified by administrators
                        </p>
                    )}
                    {isAdmin && (
                        <p className="mt-1 text-sm text-blue-600 dark:text-blue-500">
                            Admin: Order number can be modified
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-4 pt-4">
                    <button
                        type="button"
                        onClick={() => navigate('/jobcards')}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-white bg-indigo-600 dark:bg-indigo-500 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>

            {/* Embedded ATR Items section */}
            {atrItems.length > 0 && (
                <ATRItemsEmbedded
                    atrItems={atrItems.map(item => ({
                        ...item,
                        equipment: item.equipment
                          ? {
                              description: item.equipment.description ?? undefined,
                              serial_number: item.equipment.serial_number ?? undefined,
                              model_number: item.equipment.model_number ?? undefined,
                            }
                          : undefined,
                    }))}
                    isLoading={isLoadingATRItems}
                    onSync={jobCard?.order_number ? async () => {
                            try {
                                setIsSyncingATRItems(true);
                                const loadingToastId = showLoadingToast('Syncing ATR items...');
                                const syncResponse = await syncATRItems(Number(id));
                                dismissToast(loadingToastId);
                                
                                // Handle different response codes
                                const responseData = syncResponse?.data;
                                if (responseData?.code === 'NO_ASSETS_FOUND') {
                                    showSuccessToast('No ATR items found for this purchase order.');
                                    setAtrItems([]);
                                } else if (responseData?.code === 'PO_NOT_FOUND') {
                                    if (responseData?.deleted_items?.job_card) {
                                        // Job card was deleted, show success message and redirect
                                        showSuccessToast('Job card deleted as purchase order was not found. Redirecting to job cards list...');
                                        setTimeout(() => {
                                            navigate('/jobcards');
                                        }, 2000);
                                        return; // Don't continue with fetching ATR items
                                    } else {
                                        showErrorToast(responseData?.message || 'Purchase order not found');
                                        setAtrItems([]);
                                    }
                                } else if (responseData?.code === 'PO_NOT_INTERNAL') {
                                    showSuccessToast(responseData?.message || 'Purchase order is not internal');
                                    setAtrItems([]);
                                } else if (responseData?.message?.toLowerCase().includes('synced successfully')) {
                                    const message = responseData?.summary?.data_source === 'purchase_order_only' 
                                        ? 'ATR items synced successfully (from PO data only)'
                                        : 'ATR items synced successfully';
                                    showSuccessToast(message);
                                } else {
                                    showSuccessToast(responseData?.message || 'ATR items synced successfully');
                                }
                            } catch (e: unknown) {
                                const error = e as ApiError;
                                const msg = error?.response?.data?.message || error?.message || '';
                                if (msg.toLowerCase().includes('no assets found') || 
                                    msg.toLowerCase().includes('purchase order is not internal')) {
                                    showSuccessToast('No ATR items found for this order.');
                                    setAtrItems([]);
                                } else if (msg.toLowerCase().includes('purchase order not found')) {
                                    if (msg.toLowerCase().includes('deleted as they were orphaned')) {
                                        // Job card was deleted, show success message and redirect
                                        showSuccessToast('Job card deleted as purchase order was not found. Redirecting to job cards list...');
                                        setTimeout(() => {
                                            navigate('/jobcards');
                                        }, 2000);
                                        return; // Don't continue with fetching ATR items
                                    } else {
                                        showErrorToast('Purchase order not found. Please verify the order number.');
                                        setAtrItems([]);
                                    }
                                } else {
                                    showErrorToast('Failed to sync ATR items');
                                }
                            } finally {
                                setIsSyncingATRItems(false);
                            }
                        } : undefined}
                        isSyncing={isSyncingATRItems}
                        onComplete={(id) => {
                            const item = atrItems.find(i => i.id === id);
                            if (item) showATRCompletionModal(item);
                        }}
                        showCompleteActions={true}
                />
            )}

            {/* Show ATR items in main content area as well */}
            {atrItems.length > 0 && (
                <div className="mt-8">
                                    <ATRItemsBox
                    atrItems={atrItems.map(item => ({
                        ...item,
                        equipment: item.equipment
                          ? {
                              description: item.equipment.description ?? undefined,
                              serial_number: item.equipment.serial_number ?? undefined,
                              model_number: item.equipment.model_number ?? undefined,
                            }
                          : undefined,
                    }))}
                    isLoading={isLoadingATRItems}
                    draggable={true}
                    defaultPosition="bottom-left"
                    onSync={jobCard?.order_number ? async () => {
                            try {
                                setIsSyncingATRItems(true);
                                const loadingToastId = showLoadingToast('Syncing ATR items...');
                                const syncResponse = await syncATRItems(Number(id));
                                dismissToast(loadingToastId);
                                
                                // Handle different response codes
                                const responseData = syncResponse?.data;
                                if (responseData?.code === 'NO_ASSETS_FOUND') {
                                    showSuccessToast('No ATR items found for this purchase order.');
                                    setAtrItems([]);
                                } else if (responseData?.code === 'PO_NOT_FOUND') {
                                    if (responseData?.deleted_items?.job_card) {
                                        // Job card was deleted, show success message and redirect
                                        showSuccessToast('Job card deleted as purchase order was not found. Redirecting to job cards list...');
                                        setTimeout(() => {
                                            navigate('/jobcards');
                                        }, 2000);
                                        return; // Don't continue with fetching ATR items
                                    } else {
                                        showErrorToast(responseData?.message || 'Purchase order not found');
                                        setAtrItems([]);
                                    }
                                } else if (responseData?.code === 'PO_NOT_INTERNAL') {
                                    showSuccessToast(responseData?.message || 'Purchase order is not internal');
                                    setAtrItems([]);
                                } else if (responseData?.message?.toLowerCase().includes('synced successfully')) {
                                    const message = responseData?.summary?.data_source === 'purchase_order_only' 
                                        ? 'ATR items synced successfully (from PO data only)'
                                        : 'ATR items synced successfully';
                                    showSuccessToast(message);
                                } else {
                                    showSuccessToast(responseData?.message || 'ATR items synced successfully');
                                }
                            } catch (e: unknown) {
                                const error = e as ApiError;
                                const msg = error?.response?.data?.message || error?.message || '';
                                if (msg.toLowerCase().includes('no assets found') || 
                                    msg.toLowerCase().includes('purchase order is not internal')) {
                                    showSuccessToast('No ATR items found for this order.');
                                    setAtrItems([]);
                                } else if (msg.toLowerCase().includes('purchase order not found')) {
                                    if (msg.toLowerCase().includes('deleted as they were orphaned')) {
                                        // Job card was deleted, show success message and redirect
                                        showSuccessToast('Job card deleted as purchase order was not found. Redirecting to job cards list...');
                                        setTimeout(() => {
                                            navigate('/jobcards');
                                        }, 2000);
                                        return; // Don't continue with fetching ATR items
                                    } else {
                                        showErrorToast('Purchase order not found. Please verify the order number.');
                                        setAtrItems([]);
                                    }
                                } else {
                                    showErrorToast('Failed to sync ATR items');
                                }
                            } finally {
                                setIsSyncingATRItems(false);
                            }
                        } : undefined}
                        isSyncing={isSyncingATRItems}
                        onComplete={(id) => {
                            const item = atrItems.find(i => i.id === id);
                            if (item) showATRCompletionModal(item);
                        }}
                        showCompleteActions={true}
                    />
                </div>
            )}

            {/* GRN Section - only show if there are GRNs to display and not an ATERA job card */}
            {jobCard?.order_number && !isAteraJobCard(jobCard) && (
                <GrnTable
                    grns={grns}
                    onEdit={handleEditGrn}
                    onFinalize={handleFinalizeGrn}
                    onDelete={handleDeleteGrn}
                    onView={handleViewGrn}
                    onCreateNew={() => setShowGrnForm(true)}
                    showCreateButton={true}
                    showActions={isAdmin}
                    jobCardId={Number(id)}
                    isAdmin={isAdmin}
                />
            )}

            <section className="space-y-6 max-w-full">
                <div className="flex justify-between items-center relative">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Tasks</h2>
                    <div className="relative">
                        <button
                            onClick={() => setShowTaskModal(true)}
                            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 dark:bg-indigo-500 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors duration-200"
                        >
                            <Plus size={20} />
                            <span>Add Task</span>
                        </button>
                        {/* Coachmark tooltip (dismissible) */}
                        {showAddTaskCoachmark && (
                            <div className="absolute right-0 mt-2 w-80 z-10">
                                <div className="relative rounded-lg shadow-lg border p-3 bg-amber-50 border-amber-200 dark:bg-amber-900/60 dark:border-amber-700 ring-1 ring-amber-100/60 dark:ring-amber-800/60">
                                    <div className="absolute -top-2 right-6 w-4 h-4 bg-amber-50 rotate-45 border-l border-t border-amber-200 dark:bg-amber-900/60 dark:border-amber-700"></div>
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                                NOTE
                                            </p>
                                            <p className="text-sm text-amber-800 dark:text-amber-200 mt-0.5">
                                                Manually add delivery notes or record work completed.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <span className="text-xs text-amber-700 dark:text-amber-200">Tip</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                try { localStorage.setItem(`coachmark_add_task_dismissed_${id}`, '1'); } catch { /* ignore */ }
                                                setShowAddTaskCoachmark(false);
                                            }}
                                            className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-800 dark:text-amber-100 dark:hover:bg-amber-700"
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid gap-4">
                    {tasks.length > 0 ? tasks.map((task) => (
                        (() => {
                            const isATRTask = task.task_type === 'atr';
                            const canEditTask = !(isATRTask && !isAdmin);
                            return (
                        <div key={task.id} className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="flex flex-col gap-4">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900 dark:text-white break-words">{task.description}</p>
                                        {isATRTask && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 mt-1">
                                                ATR Task
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                        {canEditTask && (
                                        <button
                                            onClick={() => {
                                                setSelectedTask(task);
                                                setShowEditTaskModal(true);
                                            }}
                                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                                            title={isATRTask ? 'Edit ATR task' : 'Edit task'}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                            </svg>
                                        </button>
                                        )}
                                        {(!isATRTask || isAdmin) && (
                                            <button
                                                onClick={() => handleDeleteTask(task)}
                                                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="w-full">
                                    {task.equipment && task.equipment.length > 0 && (
                                        <div className="mt-2 w-full">
                                            <div 
                                                className="font-medium text-gray-900 dark:text-white flex items-center cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-200 py-1.5 px-3 border border-gray-200 dark:border-gray-700 rounded-t-lg bg-gray-50 dark:bg-gray-800 w-full" 
                                                onClick={() => toggleEquipmentSection(task.id)}
                                            >
                                                {expandedEquipment[task.id] ? 
                                                    <ChevronDown className="w-4 h-4 mr-2 text-indigo-500" /> : 
                                                    <ChevronRight className="w-4 h-4 mr-2 text-indigo-500" />
                                                }
                                                Equipment
                                            </div>
                                            {expandedEquipment[task.id] && (
                                                <div className="space-y-2 p-3 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg bg-white dark:bg-gray-800 w-full">
                                                    {(() => {
                                                        // Group equipment by type, subtype, and order number
                                                        const groupedEquipment = task.equipment.reduce((acc, item) => {
                                                            const typeKey = item.equipment?.equipment_type?.name || 'Unknown Type';
                                                            const subtypeKey = item.equipment?.equipment_subtype_id && 
                                                                item.equipment?.equipment_type?.subtypes?.find(
                                                                    (s: { id: number }) => s.id === item.equipment?.equipment_subtype_id
                                                                )?.name || undefined;
                                                            
                                                            const key = `${typeKey}${subtypeKey ? `:${subtypeKey}` : ''}`;
                                                            
                                                            if (!acc[key]) {
                                                                acc[key] = {
                                                                    groups: {},
                                                                    type: typeKey,
                                                                    subtype: subtypeKey
                                                                };
                                                            }
                                                            
                                                            // Further group by model and order number
                                                            const modelKey = `${item.equipment?.name || ''}|${item.equipment?.model_number || ''}|${item.order_number || ''}|${item.allocation_type || ''}|${item.store_number || ''}|${item.allocated_id || ''}`;
                                                            
                                                            if (!acc[key].groups[modelKey]) {
                                                                acc[key].groups[modelKey] = {
                                                                    item: {...item},
                                                                    count: 0
                                                                };
                                                            }
                                                            
                                                            // Increment count
                                                            acc[key].groups[modelKey].count += (item.quantity || 1);
                                                            
                                                            return acc;
                                                        }, {} as Record<string, {
                                                            groups: Record<string, {
                                                                item: TaskEquipment,
                                                                count: number
                                                            }>,
                                                            type: string,
                                                            subtype?: string
                                                        }>);
                                                        
                                                        return Object.entries(groupedEquipment).map(([key, group]) => (
                                                        <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                                            <div className="bg-gray-100 dark:bg-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                {group.type}{group.subtype ? ` > ${group.subtype}` : ''}
                                                            </div>
                                                            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                                                    {Object.values(group.groups).map((groupItem, idx) => (
                                                                    <div key={idx} className="p-2 bg-white dark:bg-gray-800">
                                                                        <p className="break-words flex items-start gap-2">
                                                                            <span className="font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-xs">
                                                                                    {groupItem.count}x
                                                                            </span>
                                                                            <span>
                                                                                    <span className="font-medium text-gray-500 dark:text-gray-400">{groupItem.item.equipment?.name}</span>
                                                                                    {groupItem.item.equipment?.serial_number && (
                                                                                    <span className="text-gray-500 dark:text-gray-400">
                                                                                            {" - "}S/N: {groupItem.item.equipment.serial_number}
                                                                                    </span>
                                                                                )}
                                                                                    {groupItem.item.equipment?.model_number && (
                                                                                    <span className="text-gray-500 dark:text-gray-400">
                                                                                            {" - "}Model: {groupItem.item.equipment.model_number}
                                                                                    </span>
                                                                                )}
                                                                                    {groupItem.item.order_number && (
                                                                                    <span className="ml-1">
                                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                                                                                                Order #{groupItem.item.order_number}
                                                                                        </span>
                                                                                    </span>
                                                                                )}
                                                                                    {groupItem.item.allocation_type === 'store' ? (
                                                                                    <span className="text-gray-500 dark:text-gray-400">
                                                                                            {" - "}Store: {groupItem.item.store_number}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-gray-500 dark:text-gray-400">
                                                                                            {" - "}Allocated to Equipment: #{groupItem.item.allocated_id}
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        ));
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {task.pictures && task.pictures.length > 0 ? (
                                        <div className="mt-4 w-full">
                                            <ImageDebugInfo
                                                images={task.pictures.map(pic => ({
                                                    id: pic.id,
                                                    url: getTaskImageUrl(pic.picture),
                                                    alt: `Task ${task.id} picture`
                                                }))}
                                                type="task"
                                            />
                                            <TaskImagesGallery
                                                images={task.pictures.map(pic => ({
                                                    id: pic.id,
                                                    url: getTaskImageUrl(pic.picture),
                                                    alt: `Task ${task.id} picture`
                                                }))}
                                                onDelete={(pictureId) => handleDeletePicture(task.id, pictureId)}
                                                readOnly={!isAdmin && jobCard?.closed}
                                                // Admins can delete task pictures even when job card is closed
                                            />
                                        </div>
                                    ) : (
                                        <div className="mt-2">
                                            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                                                No images ({task.pictures?.length || 0} images)
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                            );
                        })()
                    )) : (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">No tasks added yet</p>
                    )}
                </div>

                <CreateTaskModal
                    jobCardId={Number(id)}
                    isOpen={showTaskModal}
                    onClose={() => setShowTaskModal(false)}
                    jobcardStoreNumber={jobCard?.store?.storenumber}
                    jobcardStoreName={jobCard?.store?.name}
                    jobNumber={jobCard?.job_number}
                    onSuccess={() => {
                        setShowTaskModal(false);
                        showSuccessToast('Task added successfully');
                        // Refresh tasks
                        getTasks(Number(id)).then(res => {
                            setTasks(res.data || []);
                        }).catch(() => {
                            showErrorToast('Failed to refresh tasks');
                        });
                    }}
                />

                {hasEquipmentAccess && selectedTask && (
                    <EditTaskModal
                        jobCardId={jobCard.id}
                        task={selectedTask}
                        isOpen={showEditTaskModal}
                        onClose={() => setShowEditTaskModal(false)}
                        jobcardStoreNumber={jobCard?.store?.storenumber}
                        onSuccess={() => {
                            setShowEditTaskModal(false);
                            fetchJobCard();
                        }}
                    />
                )}
            </section>

            <section className="space-y-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Signatures</h2>
                    {(!signatures || signatures.length < 2) && (
                        <button
                            onClick={() => setShowSignatureModal(true)}
                            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 dark:bg-indigo-500 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors duration-200"
                        >
                            <Plus size={20} />
                            <span>Add Signature</span>
                        </button>
                    )}
                </div>

                <div className="grid gap-4 grid-cols-2">
                    {signatures?.map(signature => (
                        <div
                            key={signature.id}
                            className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 w-full"
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <p className="font-medium text-gray-900 dark:text-white">{signature.sign_name}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {signature.is_manager 
                                            ? (isStoreUser ? 'Supplier' : 'Manager') 
                                            : (isStoreUser ? 'Manager' : 'Technician')
                                        }
                                    </p>
                                    {/* Show supplier name for supplier signatures */}
                                    {isStoreUser && signature.is_manager && jobCard?.supplier?.name && (
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                            {jobCard.supplier.name}
                                        </p>
                                    )}
                                    {signature.picture && (
                                        <div className="bg-white rounded-lg overflow-hidden mt-2 flex items-center justify-center">
                                            <img
                                                src={getSignatureImageUrl(signature.picture)}
                                                alt="Signature"
                                                className="max-h-full"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    // Show a fallback message
                                                    const fallback = document.createElement('div');
                                                    fallback.className = 'text-gray-500 text-sm p-2';
                                                    fallback.textContent = 'Signature image not available';
                                                    target.parentNode?.appendChild(fallback);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleDeleteSignature(signature.id)}
                                    className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                    {(!signatures || signatures.length === 0) && (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">No signatures added yet</p>
                    )}
                </div>

                {signatures?.length === 2 && !jobCard?.closed && tasks.length > 0 && (
                    <div className="flex justify-end gap-2">
                        {/* Show warning if there are incomplete ATR items */}
                        {atrItems.filter(item => !item.completed).length > 0 && (
                            <div className="px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-yellow-700 dark:text-yellow-300 text-sm flex items-center gap-2">
                                <AlertTriangle size={16} />
                                <span>{atrItems.filter(item => !item.completed).length} ATR item{atrItems.filter(item => !item.completed).length > 1 ? 's' : ''} incomplete</span>
                            </div>
                        )}
                        <button
                            onClick={() => setIsCompleteModalOpen(true)}
                            className="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors duration-200 flex items-center gap-2"
                        >
                            Complete Job Card
                        </button>
                    </div>
                )}
                
                {/* Show message when no tasks exist */}
                {signatures?.length === 2 && !jobCard?.closed && tasks.length === 0 && (
                    <div className="flex justify-end">
                        <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center gap-2">
                            <span>Cannot complete: No tasks added</span>
                        </div>
                    </div>
                )}

                <CreateSignatureModal
                    jobCardId={Number(id)}
                    isOpen={showSignatureModal}
                    onClose={() => setShowSignatureModal(false)}
                    onSuccess={() => {
                        setShowSignatureModal(false);
                        showSuccessToast('Signature added successfully');
                        // Refresh signatures
                        getSignatures(Number(id)).then(res => setSignatures(res.data.data)).catch(() => {
                            showErrorToast('Failed to refresh signatures');
                        });
                    }}
                    existingSignatures={signatures || []}
                    isStoreUser={isStoreUser}
                />
            </section>


            {showCamera && (
                <button
                    onClick={handleSwapCamera}
                    className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                    <RefreshCw size={20} className="text-gray-600 dark:text-gray-400" />
                </button>
            )}

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setTaskToDelete(null);
                    setSignatureToDelete(null);
                }}
                onConfirm={signatureToDelete ? confirmDeleteSignature : confirmDeleteTask}
                itemType={signatureToDelete ? "Signature" : "Task"}
                itemName={signatureToDelete
                    ? (signatures.find(s => s.id === signatureToDelete)?.sign_name || '')
                    : (taskToDelete?.description || '')}
            />

            <ConfirmCompleteModal
                isOpen={isCompleteModalOpen}
                onClose={() => setIsCompleteModalOpen(false)}
                onConfirm={handleComplete}
                jobNumber={jobCard?.job_number || ''}
                taskCount={tasks.length}
                incompleteATRItems={atrItems.filter(item => !item.completed).map(item => ({
                    id: item.id,
                    asset: item.asset
                }))}
            />

            <TaskImageModal
                isOpen={isImageModalOpen}
                onClose={() => setIsImageModalOpen(false)}
                tasks={tasksWithImages}
                currentIndex={currentImageIndex}
                onNext={handleNextImage}
                onPrev={handlePrevImage}
            />

            <ConfirmATRCompletionModal
                isOpen={isATRCompletionModalOpen}
                onClose={() => setIsATRCompletionModalOpen(false)}
                onConfirm={(completionAction) => {
                    if (selectedATRItem) {
                        handleCompleteATRItem(selectedATRItem.id, completionAction);
                    }
                }}
                assetName={selectedATRItem?.asset || ''}
            />

            {/* <SerialNumberModal
                isOpen={isSerialNumberModalOpen}
                onClose={() => setIsSerialNumberModalOpen(false)}
                equipment={selectedEquipment}
                onSuccess={() => {
                    setIsSerialNumberModalOpen(false);
                    showSuccessToast('Serial number updated successfully');
                    // Refresh ATR items
                    fetchATRItems();
                }}
            /> */}

            <DeleteConfirmationModal
                isOpen={isDeleteJobCardModalOpen}
                onClose={() => setIsDeleteJobCardModalOpen(false)}
                onConfirm={confirmDeleteJobCard}
                itemType="Job Card"
                itemName={jobCard?.job_number || ''}
            />

            {/* Link Job Card Modals */}
            {jobCard && (
                <>
                    <LinkJobCardModal
                        isOpen={showLinkJobCardModal}
                        onClose={() => setShowLinkJobCardModal(false)}
                        poJobCard={jobCard}
                        onSelectJobCard={handleSelectTargetJobCard}
                    />

                    {selectedTargetJobCard && (
                        <ConfirmLinkJobCardModal
                            isOpen={showConfirmLinkModal}
                            onClose={() => {
                                setShowConfirmLinkModal(false);
                                setSelectedTargetJobCard(null);
                            }}
                            poJobCard={jobCard}
                            targetJobCard={selectedTargetJobCard}
                            onConfirm={handleConfirmLink}
                            isLoading={isLinking}
                        />
                    )}
                </>
            )}

            {/* GRN Modals */}
            {showGrnForm && (
                <GrnForm
                    jobCardId={jobCard?.id || 0}
                    grn={editingGrn}
                    onSave={handleGrnSave}
                    onClose={() => {
                        setShowGrnForm(false);
                        setEditingGrn(null);
                    }}
                />
            )}

            {showGrnFinalizeModal && grnToFinalize && (
                <GrnFinalizeModal
                    grn={grnToFinalize}
                    onCancel={() => {
                        setShowGrnFinalizeModal(false);
                        setGrnToFinalize(null);
                    }}
                    onFinalize={handleGrnFinalized}
                />
            )}

            {/* GRN View Modal */}
            {viewingGrn && (
                <GrnViewModal
                    grn={viewingGrn}
                    isOpen={!!viewingGrn}
                    onClose={() => setViewingGrn(null)}
                />
            )}
        </div>
    );
} 